import api from '../utils/axiosConfig';
import {
  AIAccuracyMetrics,
  AIMetricsSnapshot,
  ErrorBudgetStatus,
  MTTRMetrics,
  MTTDMetrics,
  DataQualityMetrics,
  AgentUptimeStats,
  BaselineComparison,
  ErrorBudgetAcknowledgment,
} from '../types';

export class AIMetricsService {
  static async getAIAccuracy(clientId: string, days: number = 90): Promise<AIAccuracyMetrics> {
    try {
      const response = await api.get('/playbook/stats/ai-accuracy', {
        params: { client_id: clientId, days },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch AI accuracy metrics');
    }
  }

  static async getMTTR(clientId: string, days: number = 90): Promise<MTTRMetrics> {
    try {
      const response = await api.get('/playbook/stats/mttr', {
        params: { client_id: clientId, days },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch MTTR metrics');
    }
  }

  static async getErrorBudget(clientId: string): Promise<ErrorBudgetStatus> {
    try {
      const response = await api.get('/playbook/stats/error-budget', {
        params: { client_id: clientId },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch error budget status');
    }
  }

  static async saveErrorBudgetThreshold(clientId: string, threshold: number): Promise<void> {
    // Fetch the existing policy so we don't clobber other fields (severities, custom_rules, settings)
    const policyRes = await api.get(`/clients/${clientId}/policy`).catch(() => ({ data: {} }));
    const existing = policyRes.data ?? {};

    await api.put(`/clients/${clientId}/policy`, {
      auto_ticket_severities: existing.auto_ticket_severities ?? [],
      custom_rules: existing.custom_rules ?? [],
      settings: {
        ...(existing.settings ?? {}),
        ai_error_budget_threshold: threshold,
      },
    });
  }

  static async getSnapshots(clientId: string, limit: number = 52): Promise<AIMetricsSnapshot[]> {
    try {
      const response = await api.get('/playbook/stats/snapshots', {
        params: { client_id: clientId, limit },
      });
      return response.data.snapshots ?? [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch snapshots');
    }
  }

  static async downloadTrainingData(clientId: string): Promise<void> {
    const response = await api.get('/playbook/export/training-data', {
      params: { client_id: clientId },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/x-ndjson' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-data-${clientId}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  }

  static async getMTTD(clientId: string, days: number = 90): Promise<MTTDMetrics> {
    try {
      const response = await api.get('/playbook/stats/mttd', {
        params: { client_id: clientId, days },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch MTTD metrics');
    }
  }

  static async getDataQuality(clientId: string, days: number = 90): Promise<DataQualityMetrics> {
    try {
      const response = await api.get('/playbook/stats/data-quality', {
        params: { client_id: clientId, days },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch data quality metrics');
    }
  }

  static async getAgentUptime(): Promise<AgentUptimeStats> {
    try {
      const response = await api.get('/playbook/agent-uptime');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch agent uptime');
    }
  }

  static async getBaselineComparison(clientId: string, days: number = 30): Promise<BaselineComparison> {
    try {
      const response = await api.get('/playbook/baseline/compare', {
        params: { client_id: clientId, days },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch baseline comparison');
    }
  }

  static async declareBaseline(data: {
    client_id: string;
    period_start: string;
    period_end: string;
    metrics?: {
      avg_mttr_hours?: number | null;
      avg_mttd_hours?: number | null;
      total_tickets_in_period?: number | null;
      total_resolved?: number | null;
      data_quality?: { root_cause_rate?: number | null; mitre_tags_rate?: number | null };
    };
  }): Promise<any> {
    try {
      const response = await api.post('/playbook/baseline/declare', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to declare baseline');
    }
  }

  static async acknowledgeErrorBudget(
    clientId: string,
    note?: string,
  ): Promise<ErrorBudgetAcknowledgment> {
    try {
      const response = await api.post('/playbook/error-budget/acknowledge', {
        client_id: clientId,
        ...(note ? { acknowledged_note: note } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to acknowledge error budget');
    }
  }

  static async getErrorBudgetAcknowledgment(clientId: string): Promise<ErrorBudgetAcknowledgment | null> {
    try {
      const policyRes = await api.get(`/clients/${clientId}/policy`);
      const settings = policyRes.data?.settings ?? {};
      if (!settings.ai_error_budget_acknowledged_at) return null;
      return {
        threshold: settings.ai_error_budget_threshold ?? null,
        acknowledged_by: settings.ai_error_budget_acknowledged_by_name ?? null,
        acknowledged_at: settings.ai_error_budget_acknowledged_at ?? null,
        acknowledged_note: settings.ai_error_budget_acknowledged_note ?? null,
      };
    } catch {
      return null;
    }
  }
}
